// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/ui.tsx
// 🔧 PURPOSE: I pezzi comuni della nuova Home, nello stile delle email del
//   channel manager: occhiello d'oro, titolo in Fraunces, bottoni maiuscoli
//   espresso / oro / a filo, il riquadro crema col filo d'oro, quello
//   "da pagare", le righe di un elenco e le pillole.
//   Ognuno porta già il suo tema scuro (media query, scale stone) e bersagli
//   di almeno 44px: le viste della Home li compongono senza ripetere classi.
//   Contrasti (AA) misurati sui colori di tailwind.config.js.
// ============================================

import React from 'react';
import { Link, To } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

// ---- Classi condivise ----------------------------------------------------

/** Il testo "forte" della Home: espresso sul chiaro, stone-100 sullo scuro. */
export const INK_TEXT = 'text-nva-espresso dark:text-stone-100';
/** Il testo secondario: nva-muted (non il grigio delle email, 4.2:1 sul crema). */
export const MUTED_TEXT = 'text-nva-muted dark:text-stone-400';
/** L'anello del fuoco da tastiera, come nelle pagine già rifatte. */
export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400';
/** Sull'espresso il blu mare non si vede: lì l'anello è d'oro. */
export const FOCUS_RING_ON_DARK = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-gold';

// ---- Occhiello, filo d'oro, titoli ------------------------------------------

type EyebrowTone = 'gold' | 'onDark' | 'muted';

const EYEBROW_TONE: Record<EyebrowTone, string> = {
  // #7A5E1E e non l'oro delle email (#8C6D1F): quello sul crema fa 4.1:1.
  gold: 'text-nva-gold-text dark:text-nva-gold-dark',
  // Sull'intestazione espresso (o notte, in scuro).
  onDark: 'text-nva-gold-light',
  // Le didascalie "ESPERIENZE E NOLEGGI" fra un blocco e l'altro.
  muted: 'text-nva-muted dark:text-stone-400',
};

interface EyebrowProps {
  children: React.ReactNode;
  tone?: EyebrowTone;
  className?: string;
  as?: 'span' | 'p';
}

/** L'occhiello maiuscolo e spaziato sopra i titoli ("OGGI · MERCOLEDÌ 23"). */
export const Eyebrow: React.FC<EyebrowProps> = ({ children, tone = 'gold', className, as = 'span' }) => {
  const Tag = as;
  return (
    <Tag className={cn('block text-[11px] font-bold uppercase leading-snug tracking-eyebrow', EYEBROW_TONE[tone], className)}>
      {children}
    </Tag>
  );
};

/** Il filo d'oro corto delle email, sopra i saluti e nel footer. */
export const GoldRule: React.FC<{ className?: string; thin?: boolean }> = ({ className, thin = false }) => (
  <span aria-hidden="true" className={cn('block w-9 bg-nva-gold', thin ? 'h-px' : 'h-0.5', className)} />
);

interface SectionTitleProps {
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  /** L'id del titolo, per l'aria-labelledby della sezione. */
  id?: string;
  /** A destra del titolo: di solito un MoreLink ("Tutti ›"). */
  action?: React.ReactNode;
  as?: 'h2' | 'h3';
  className?: string;
}

/** Occhiello d'oro + titolo in Fraunces 23px, come Esplora, Partner, Eventi. */
export const SectionTitle: React.FC<SectionTitleProps> = ({ title, eyebrow, id, action, as = 'h2', className }) => {
  const Tag = as;
  return (
    <div className={cn('flex items-end justify-between gap-3', className)}>
      <div className="flex min-w-0 flex-col gap-1">
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Tag
          id={id}
          tabIndex={-1}
          className={cn('font-display text-[23px] font-semibold leading-tight outline-none', INK_TEXT)}
        >
          {title}
        </Tag>
      </div>
      {action ? <div className="flex-shrink-0">{action}</div> : null}
    </div>
  );
};

interface SectionProps {
  children: React.ReactNode;
  /** L'id del titolo della sezione (vedi SectionTitle). */
  labelledBy?: string;
  /** Per i caroselli orizzontali: niente margine a destra, scorrono fino al bordo. */
  flushRight?: boolean;
  className?: string;
  id?: string;
}

/** Una sezione della Home: 16px ai lati, 26px sopra, 12px fra i blocchi. */
export const Section: React.FC<SectionProps> = ({ children, labelledBy, flushRight = false, className, id }) => (
  <section
    id={id}
    aria-labelledby={labelledBy}
    className={cn('flex flex-col gap-3 pl-4 pt-[26px]', flushRight ? 'pr-0' : 'pr-4', className)}
  >
    {children}
  </section>
);

// ---- Bottoni ------------------------------------------------------------------

/**
 * Dove porta un'azione: una pagina dell'app (`to`, con lo stato del router),
 * un indirizzo esterno (`href`) oppure niente (`onClick`, un <button>).
 */
type ActionTarget =
  | {
      to: To;
      state?: unknown;
      replace?: boolean;
      href?: never;
      onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    }
  | {
      href: string;
      to?: never;
      target?: string;
      rel?: string;
      onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    }
  | {
      to?: never;
      href?: never;
      onClick?: React.MouseEventHandler<HTMLButtonElement>;
      type?: 'button' | 'submit';
      disabled?: boolean;
    };

type ActionCommon = React.AriaAttributes & {
  children: React.ReactNode;
  className?: string;
  id?: string;
  title?: string;
};

export type HomeActionProps = ActionTarget & ActionCommon;

/** Rende l'azione con il tag giusto: Link, <a> o <button>. */
function renderAction(props: HomeActionProps, classes: string): React.ReactElement {
  if (props.to !== undefined) {
    const { to, state, replace, onClick, children, className: _c, ...rest } = props;
    return (
      <Link to={to} state={state} replace={replace} onClick={onClick} className={classes} {...rest}>
        {children}
      </Link>
    );
  }
  if (props.href !== undefined) {
    const { href, target, rel, onClick, children, className: _c, ...rest } = props;
    // Fuori dall'app: una scheda nuova e niente window.opener.
    const external = target === '_blank';
    return (
      <a
        href={href}
        target={target}
        rel={rel || (external ? 'noopener noreferrer' : undefined)}
        onClick={onClick}
        className={classes}
        {...rest}
      >
        {children}
      </a>
    );
  }
  const { onClick, type, disabled, children, className: _c, ...rest } = props;
  return (
    <button type={type || 'button'} onClick={onClick} disabled={disabled} className={classes} {...rest}>
      {children}
    </button>
  );
}

type ButtonVariant = 'ink' | 'gold' | 'line' | 'lineOnDark' | 'due';

/** Maiuscolo, spaziato, 12.5px extrabold, alto almeno 44px (come i mockup). */
const BUTTON_BASE =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-4 text-center text-[12.5px] font-extrabold uppercase leading-tight tracking-button transition-colors motion-safe:active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none';

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  // In scuro si inverte, come le pillole attive di Esplora (16:1).
  ink: cn('bg-nva-espresso text-nva-bone hover:bg-[#3a342c] dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white', FOCUS_RING),
  // Espresso sull'oro: 6.2:1, uguale nei due temi.
  gold: cn('bg-nva-gold text-nva-espresso hover:bg-[#cfae5a]', FOCUS_RING),
  line: cn('border border-nva-foot bg-transparent text-nva-espresso dark:border-stone-600 dark:text-stone-100', FOCUS_RING),
  // A filo sull'espresso (card della reception, intestazione).
  lineOnDark: cn('border border-nva-bone/30 bg-transparent text-nva-bone', FOCUS_RING_ON_DARK),
  // "Paga": terracotta con il testo bianco (5:1).
  due: cn('bg-nva-due-btn text-white hover:bg-[#9e4724]', FOCUS_RING),
};

type ButtonProps = HomeActionProps & {
  /** A tutta larghezza. */
  full?: boolean;
};

function makeButton(variant: ButtonVariant, name: string): React.FC<ButtonProps> {
  const Component: React.FC<ButtonProps> = ({ full = false, ...props }) =>
    renderAction(props as HomeActionProps, cn(BUTTON_BASE, BUTTON_VARIANT[variant], full && 'w-full', props.className));
  Component.displayName = name;
  return Component;
}

/** Bottone pieno espresso ("FAI IL CHECK-IN", "GIOCA"). */
export const InkButton = makeButton('ink', 'InkButton');
/** Bottone oro ("REGALA", "SCRIVI ALLA RECEPTION" sull'espresso). */
export const GoldButton = makeButton('gold', 'GoldButton');
/** Bottone a filo sul chiaro. */
export const LineButton = makeButton('line', 'LineButton');
/** Bottone a filo sull'espresso ("ASSISTENTE AI · RISPOSTA IMMEDIATA"). */
export const LineOnDarkButton = makeButton('lineOnDark', 'LineOnDarkButton');
/** Bottone terracotta del riquadro "da pagare". */
export const DueButton = makeButton('due', 'DueButton');

/** "Tutti ›" a destra del titolo di una sezione. */
export const MoreLink: React.FC<HomeActionProps> = (props) =>
  renderAction(
    {
      ...props,
      children: (
        <>
          {props.children}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </>
      ),
    } as HomeActionProps,
    cn('inline-flex min-h-[44px] items-center gap-0.5 rounded-lg px-1 text-[13.5px] font-bold', INK_TEXT, FOCUS_RING, props.className),
  );

// ---- Card, riquadri, righe ----------------------------------------------------

interface CardShellProps {
  children: React.ReactNode;
  /**
   * La card sovrapposta all'intestazione (quella del soggiorno): ombra sul
   * chiaro; in scuro l'ombra sparisce e stone-900 sulla notte (#1a1a1a) fa
   * 1:1, quindi il fondo sale a stone-800 con il bordo stone-700.
   */
  raised?: boolean;
  as?: 'div' | 'section' | 'article' | 'li';
  className?: string;
  id?: string;
  'aria-labelledby'?: string;
}

/** La card della Home: avorio con il bordo sabbia, 18px di raggio. */
export const CardShell: React.FC<CardShellProps> = ({ children, raised = false, as = 'div', className, ...rest }) => {
  const Tag = as;
  return (
    <Tag
      className={cn(
        'rounded-[18px] border border-nva-edge bg-nva-card dark:border-stone-800 dark:bg-stone-900',
        raised && 'shadow-nva-lift dark:border-stone-700 dark:bg-stone-800 dark:shadow-none',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
};

type IconTone = 'cream' | 'ink' | 'card';

const ICON_TONE: Record<IconTone, string> = {
  cream: 'bg-nva-cream text-nva-espresso dark:bg-stone-800 dark:text-nva-gold-dark',
  ink: 'bg-nva-espresso text-nva-gold dark:bg-stone-700 dark:text-nva-gold-dark',
  card: 'bg-nva-card text-nva-espresso dark:bg-stone-900 dark:text-nva-gold-dark',
};

/** Il cerchio dell'icona (40px di solito, 34 nelle tessere piccole). */
export const IconCircle: React.FC<{ children: React.ReactNode; tone?: IconTone; size?: 34 | 38 | 40 | 44 | 46; className?: string }> = ({
  children, tone = 'cream', size = 40, className,
}) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={cn('relative flex flex-shrink-0 items-center justify-center rounded-full [&>svg]:h-[18px] [&>svg]:w-[18px]', ICON_TONE[tone], className)}
  >
    {children}
  </span>
);

interface CalloutProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  /** Sotto il testo, allineata a sinistra: di solito un InkButton. */
  action?: React.ReactNode;
  className?: string;
  role?: string;
}

/** Il riquadro crema con il filo d'oro a sinistra, come le note delle email. */
export const Callout: React.FC<CalloutProps> = ({ children, title, icon, action, className, role }) => (
  <div
    role={role}
    className={cn(
      'flex flex-col gap-2.5 rounded-l-[4px] rounded-r-2xl border-l-[3px] border-nva-gold bg-nva-cream p-3.5 dark:bg-nva-cream-dark',
      className,
    )}
  >
    <div className="flex items-start gap-3">
      {icon ? <IconCircle tone="card">{icon}</IconCircle> : null}
      <div className="flex min-w-0 flex-col gap-0.5">
        {title ? <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{title}</span> : null}
        {children ? (
          <div className="text-[13.5px] leading-[1.4] text-nva-muted dark:text-stone-300">{children}</div>
        ) : null}
      </div>
    </div>
    {action ? <div className="flex flex-wrap gap-2">{action}</div> : null}
  </div>
);

interface DueBlockProps {
  /** "DA PAGARE", "DA FARE OGGI". */
  badge: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  /** L'importo in Fraunces, a destra ("€ 8,00"). */
  amount?: React.ReactNode;
  /** Il bottone, a destra sotto l'importo (o sotto il testo senza importo). */
  action?: React.ReactNode;
  /** Una riga piccola a tutta larghezza sotto (per la tassa: il codice arriva dopo il pagamento). */
  note?: React.ReactNode;
  /** h3 quando il riquadro apre un punto della sezione ("Da fare"). */
  titleAs?: 'span' | 'h3';
  className?: string;
}

/**
 * Il riquadro "da pagare" delle email: fondo pesca, filo terracotta, testo
 * #9C5A22 (4.7:1). Per la tassa di soggiorno e per il check-in urgente.
 */
export const DueBlock: React.FC<DueBlockProps> = ({ badge, title, desc, amount, action, note, titleAs = 'span', className }) => {
  const Title = titleAs;
  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-l-[4px] rounded-r-2xl border-l-[3px] border-nva-due-edge bg-nva-due-bg p-3.5 dark:bg-nva-due-bg-dark',
        className,
      )}
    >
      <div className={cn('flex gap-3', amount ? 'items-center' : 'flex-col')}>
        <div className="flex min-w-0 flex-grow flex-col gap-0.5">
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-nva-due-ink dark:text-nva-due-ink-dark">
            {badge}
          </span>
          <Title className={cn('m-0 text-[15px] font-bold leading-snug', INK_TEXT)}>{title}</Title>
          {desc ? <span className="text-[13px] leading-snug text-nva-muted dark:text-stone-300">{desc}</span> : null}
        </div>
        {amount || action ? (
          <div className={cn('flex flex-shrink-0 gap-1.5', amount ? 'flex-col items-end' : 'flex-row')}>
            {amount ? (
              <span className="font-display text-[22px] font-bold leading-none tabular-nums text-nva-due-ink dark:text-nva-due-ink-dark">
                {amount}
              </span>
            ) : null}
            {action}
          </div>
        ) : null}
      </div>
      {/* div, non p: la nota puo' essere un riquadro (il motivo del check-in da rifare). */}
      {note ? <div className="m-0 text-[12.5px] leading-snug text-nva-muted dark:text-stone-300">{note}</div> : null}
    </div>
  );
};

type RowLinkProps = ActionTarget & {
  icon: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** Prima della freccia: un badge, un conteggio. */
  trailing?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
};

/**
 * Una riga di elenco: icona nel cerchio crema, titolo, sottotitolo e la
 * freccia. Alta almeno 60px. Va dentro un RowList (i separatori li mette lui).
 */
export const RowLink: React.FC<RowLinkProps> = ({ icon, title, sub, trailing, className, ...target }) =>
  renderAction(
    {
      ...(target as ActionTarget),
      children: (
        <>
          <IconCircle>{icon}</IconCircle>
          <span className="flex min-w-0 flex-col gap-px">
            <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{title}</span>
            {sub ? <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{sub}</span> : null}
          </span>
          <span className="ml-auto flex flex-shrink-0 items-center gap-2">
            {trailing}
            <ChevronRight className="h-[18px] w-[18px] text-nva-muted dark:text-stone-500" aria-hidden="true" />
          </span>
        </>
      ),
    } as HomeActionProps,
    cn(
      'flex min-h-[60px] w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-nva-bone/70 dark:hover:bg-stone-800/60',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400',
      className,
    ),
  );

/** La card che contiene le RowLink, con il separatore sottile fra una e l'altra. */
export const RowList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <CardShell className={cn('divide-y divide-nva-hairline overflow-hidden dark:divide-stone-800', className)}>
    {children}
  </CardShell>
);

// ---- Pillole e badge --------------------------------------------------------------

type PillTone = 'ink' | 'bone' | 'gold' | 'due' | 'cream';

const PILL_TONE: Record<PillTone, string> = {
  // Oro sull'espresso (6.2:1); in scuro si inverte come i bottoni (5.6:1).
  ink: 'bg-nva-espresso text-nva-gold dark:bg-stone-100 dark:text-nva-gold-text',
  // Sopra le foto ("PREZZI SPECIALI").
  bone: 'bg-nva-bone text-nva-espresso dark:bg-stone-900 dark:text-stone-100',
  gold: 'bg-nva-gold text-nva-espresso',
  // Testo espresso sul filo terracotta: 4.7:1 (bianco farebbe 3.2).
  due: 'bg-nva-due-edge text-nva-espresso',
  cream: 'bg-nva-cream text-nva-espresso dark:bg-nva-cream-dark dark:text-nva-gold-dark',
};

/** La pillola maiuscola ("PORTATO IN CASA", "IN ATTESA DI CONFERMA"). */
export const Pill: React.FC<{ children: React.ReactNode; tone?: PillTone; className?: string }> = ({
  children, tone = 'ink', className,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 self-start rounded-full px-[9px] py-1 text-[10.5px] font-extrabold uppercase leading-none tracking-[0.1em]',
      PILL_TONE[tone],
      className,
    )}
  >
    {children}
  </span>
);

/**
 * Il pallino dei messaggi non letti (bianco su terracotta, 5:1). Il testo
 * per i lettori di schermo lo passa chi lo usa (`label`), il numero è solo
 * per gli occhi.
 */
export const UnreadBadge: React.FC<{ count: number; label: string; className?: string }> = ({ count, label, className }) => {
  if (!(count > 0)) return null;
  return (
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-nva-bone bg-nva-due-btn px-1 text-[10.5px] font-extrabold leading-none text-white dark:border-stone-800',
        className,
      )}
    >
      <span aria-hidden="true">{count > 9 ? '9+' : count}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
};
